import { format } from "date-fns";
import { RequestApiPrivate } from "../../component/api/axios";
import type { ProcessStatusDatum } from "../types/report.types";
import type {
  RequestListRow,
  RequestListStatus,
  RequestStepEntry
} from "../types/report.types";

async function fetchProcessReport<T>(
  path: string,
  params?: Record<string, unknown>
): Promise<T> {
  const { data } = await RequestApiPrivate.get(`process-reports/${path}`, {
    params,
    headers: { "Accept-Language": "vn" }
  });
  return data.data;
}

async function postProcessReport<T>(
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const { data } = await RequestApiPrivate.post(
    `process-reports/${path}`,
    body,
    {
      headers: { "Accept-Language": "vn" }
    }
  );
  return data.data;
}

export interface TicketRequestsFilters {
  startDate?: string;
  endDate?: string;
  processIds?: number[];
  workspaceIds?: number[];
  /** Chọn tay — rỗng khi chưa chọn ai, BE AND thu hẹp. */
  creatorIds?: number[];
  /** Toàn bộ user thuộc workspaceIds đang chọn (account-service) — BE chỉ OR-mở rộng khi creatorIds
   *  rỗng, không bao giờ AND. Xem ReportFilterValues.allSelectableUserIds. */
  departmentMemberIds?: number[];
  /** Đơn vị level 5 đang chọn đã roll-up lên phòng ban (level 4) — FE tự tính (accessibleTree.ts
   *  resolveDepartmentWorkspaceIds), thay cho M_deptRollup all-time cũ ở BE. Xem
   *  ReportFilterValues.departmentWorkspaceIds. */
  departmentWorkspaceIds?: number[];
}

export function filtersToParams(
  filters: TicketRequestsFilters
): Record<string, unknown> {
  return {
    start_date: filters.startDate,
    end_date: filters.endDate,
    process_ids: filters.processIds,
    workspace_ids: filters.workspaceIds,
    creator_ids: filters.creatorIds,
    department_member_ids: filters.departmentMemberIds,
    department_workspace_ids: filters.departmentWorkspaceIds
  };
}

export interface TicketRequestsStatusSummaryAlert {
  processing: number;
  calculated_at: number;
}

/**
 * Endpoint riêng (spec §6, 07/08/2026) — không nhận mode/creator_ids/workspace_ids, tính theo
 * all-time accessible scope của viewer. KHÔNG dùng chung với getTicketRequestsStatusSummaryKpi
 * (endpoint cũ, không phân quyền all-time).
 */
export const getTicketRequestsStatusSummaryAlert =
  (): Promise<TicketRequestsStatusSummaryAlert> =>
    fetchProcessReport<TicketRequestsStatusSummaryAlert>(
      "tickets-processing-count"
    );

export interface TicketRequestsCompareToPreviousMonth {
  diff: number;
  direction: "up" | "down" | "same";
  previous_month_label: string;
}

export interface TicketRequestsStatusSummaryKpi {
  total_requests: number;
  completed: number;
  completed_percent: number;
  processing: number;
  processing_percent: number;
  rejected: number;
  rejected_percent: number;
  compare_previous_month: TicketRequestsCompareToPreviousMonth | null;
  calculated_at: number;
}

export const getTicketRequestsStatusSummaryKpi = (
  filters: TicketRequestsFilters = {}
): Promise<TicketRequestsStatusSummaryKpi> =>
  postProcessReport<TicketRequestsStatusSummaryKpi>(
    "ticket-requests-status-summary",
    {
      mode: "kpi",
      ...filtersToParams(filters)
    }
  );

interface TicketRequestsStatusByProcessItem {
  process_group_key: string;
  process_name: string;
  total: number;
  completed: number;
  processing: number;
  rejected: number;
}

interface TicketRequestsStatusByProcessResponse {
  items: TicketRequestsStatusByProcessItem[];
  total_process_matched: number;
}

export const getTicketRequestsStatusByProcess = async (
  top = 20,
  filters: TicketRequestsFilters = {}
): Promise<ProcessStatusDatum[]> => {
  const { items } =
    await postProcessReport<TicketRequestsStatusByProcessResponse>(
      "ticket-requests-status-by-process",
      { top, ...filtersToParams(filters) }
    );

  return (items ?? []).map((item) => ({
    processName: item.process_name,
    completed: item.completed,
    inProgress: item.processing,
    rejected: item.rejected
  }));
};

interface TicketRequestStep {
  step_id: number;
  step_name: string;
  step_type: number | null;
  depth: number;
  child: {
    ticket_request_id: number;
    process_name: string | null;
    current_steps: TicketRequestStep[];
  } | null;
  truncated_by_max_depth: boolean;
}

interface TicketRequestListItem {
  id: number;
  code: string;
  process_name: string | null;
  ticket_name: string;
  progress: {
    current: number | number[];
    total: number;
    is_parallel: boolean;
  };
  approval_status: number;
  is_overdue: boolean;
  current_steps: TicketRequestStep[];
  assignees: Array<{ id: number; name: string }>;
  step_due_date: number | null;
  finish_date: number | null;
}

interface TicketRequestsListResponse {
  list: TicketRequestListItem[];
  page: number;
  limit: number;
  totalItem: number;
}

export interface TicketRequestsListFilters extends TicketRequestsFilters {
  page?: number;
  limit?: number;
}

export interface TicketRequestsListResult {
  rows: RequestListRow[];
  page: number;
  limit: number;
  totalItem: number;
}

/** Mirrors BE `TicketRequest::APPROVAL_STATUS_*` (see TicketRequest.php). */
const APPROVAL_CODE_TO_STATUS: Record<number, RequestListStatus> = {
  1: "in_progress",
  2: "completed",
  3: "rejected"
};

function mapApprovalStatus(approvalStatus: number): RequestListStatus {
  return APPROVAL_CODE_TO_STATUS[approvalStatus] ?? "unknown";
}

function formatDueDate(unixSeconds: number | null): string {
  return unixSeconds ? format(new Date(unixSeconds * 1000), "dd/MM/yyyy") : "-";
}

const MAX_CHILD_NESTING_DEPTH = 5;

function buildChildStepLines(
  steps: TicketRequestStep[],
  depth: number
): string[] {
  if (depth > MAX_CHILD_NESTING_DEPTH || steps.length === 0) return [];
  const stepNames = steps.map((s) => s.step_name).join(", ");
  const nextLevelSteps = steps.flatMap((s) => s.child?.current_steps ?? []);
  return [stepNames, ...buildChildStepLines(nextLevelSteps, depth + 1)];
}

function mapTicketToRow(ticket: TicketRequestListItem): RequestListRow {
  const { current } = ticket.progress;
  const progressTotal = ticket.progress.total;
  const dueDate = formatDueDate(ticket.step_due_date);

  const progressDoneAt = (index: number): number =>
    Array.isArray(current)
      ? current[index] ?? Math.max(0, ...current)
      : current;

  const steps: RequestStepEntry[] = ticket.current_steps.length
    ? ticket.current_steps.map((step, index) => ({
        progressDone: progressDoneAt(index),
        progressTotal,
        stepName: step.step_name,
        subSteps: step.child
          ? buildChildStepLines(step.child.current_steps, 1)
          : undefined,
        assignee: ticket.assignees[index]?.name ?? "-",
        dueDate
      }))
    : [
        {
          progressDone: progressDoneAt(0),
          progressTotal,
          stepName: "-",
          assignee: ticket.assignees[0]?.name ?? "-",
          dueDate
        }
      ];

  return {
    id: ticket.id,
    processName: ticket.process_name ?? "-",
    requestName: ticket.ticket_name,
    status: mapApprovalStatus(ticket.approval_status),
    isOverdue: ticket.is_overdue,
    steps,
    finishDate: formatDueDate(ticket.finish_date)
  };
}

export const getTicketRequestsList = async (
  filters: TicketRequestsListFilters = {}
): Promise<TicketRequestsListResult> => {
  const { page = 1, limit = 20 } = filters;
  const { list, ...pagination } =
    await postProcessReport<TicketRequestsListResponse>("ticket-requests", {
      ...filtersToParams(filters),
      page,
      limit
    });
  return { rows: (list ?? []).map(mapTicketToRow), ...pagination };
};
